import { useRef, type ReactNode } from 'react';
import { useServices } from '../app/services.js';
import { FontSelect } from './FontSelect.jsx';
import { useFocusTrap } from './useFocusTrap.js';

/**
 * H2: lightweight in-reader popover for display-affecting settings —
 * everything applies live through the settings store. The Reader suspends
 * playback while it is open (controller.suspendForUi) and resumes on close
 * per the existing resume rules. Focus trap + Esc per §6.5.
 */
export function QuickSettings({ onClose }: { onClose: () => void }): ReactNode {
  const { t, settings, updateSettings } = useServices();
  const panelRef = useRef<HTMLDivElement>(null);

  useFocusTrap(panelRef, onClose);

  return (
    <div
      className="quick-settings fade-up"
      role="dialog"
      aria-modal="true"
      aria-label={t('reader.quickSettings')}
      ref={panelRef}
    >
      <div className="qs-row">
        <span className="qs-label">{t('settings.font')}</span>
        <FontSelect />
      </div>
      <div className="qs-row">
        <span className="qs-label">{t('settings.fontSize')}</span>
        <div className="seg">
          {([0, 1, 2, 3] as const).map((size) => (
            <button
              key={size}
              className={settings.fontSize === size ? 'active' : ''}
              style={{ fontSize: `${0.75 + size * 0.12}rem` }}
              onClick={() => updateSettings({ fontSize: size })}
            >
              A
            </button>
          ))}
        </div>
      </div>
      <div className="qs-row">
        <span className="qs-label">{t('settings.theme')}</span>
        <div className="seg">
          {(['dark', 'light', 'amoled'] as const).map((theme) => (
            <button
              key={theme}
              className={settings.theme === theme ? 'active' : ''}
              onClick={() => updateSettings({ theme })}
            >
              {t(`settings.theme.${theme}`)}
            </button>
          ))}
        </div>
      </div>
      <div className="qs-row">
        <span className="qs-label">{t('settings.orpColor')}</span>
        <div className="row-controls">
          <input
            type="color"
            aria-label={t('settings.orpColor')}
            value={settings.orpColor ?? '#e74c3c'}
            onChange={(e) => updateSettings({ orpColor: e.target.value })}
          />
          {settings.orpColor !== null && (
            <button
              className="btn btn-sm btn-ghost"
              onClick={() => updateSettings({ orpColor: null })}
            >
              ↺
            </button>
          )}
        </div>
      </div>
      <div className="qs-row">
        <span className="qs-label">{t('settings.chunk')}</span>
        <div className="seg">
          {([1, 2, 3] as const).map((size) => (
            <button
              key={size}
              className={settings.chunkSize === size ? 'active' : ''}
              onClick={() => updateSettings({ chunkSize: size })}
            >
              {size}
            </button>
          ))}
        </div>
      </div>
      {settings.chunkSize > 1 && (
        <div className="qs-row">
          <span className="qs-label">{t('settings.chunkLayout')}</span>
          <div className="seg">
            {(['vertical', 'horizontal'] as const).map((layout) => (
              <button
                key={layout}
                className={settings.chunkLayout === layout ? 'active' : ''}
                onClick={() => updateSettings({ chunkLayout: layout })}
              >
                {t(`settings.chunkLayout.${layout}`)}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="qs-row">
        <span className="qs-label">{t('settings.warmup')}</span>
        <div className="seg">
          <button
            className={settings.warmupEnabled ? 'active' : ''}
            onClick={() => updateSettings({ warmupEnabled: true })}
          >
            {t('common.on')}
          </button>
          <button
            className={!settings.warmupEnabled ? 'active' : ''}
            onClick={() => updateSettings({ warmupEnabled: false })}
          >
            {t('common.off')}
          </button>
        </div>
      </div>
      <button className="btn btn-sm" style={{ alignSelf: 'flex-end' }} onClick={onClose}>
        {t('common.close')}
      </button>
    </div>
  );
}
